import { memo, forwardRef, useMemo, useState, useCallback, type ReactNode } from 'react';
import { makeStyles, tokens, mergeClasses, Tooltip, shorthands } from '@coreai/fluentui-react';
import { ottoTokens } from '@/theme/ottoTokens';
import { ClampedLabel } from './ClampedLabel';
import {
  ErrorCircleFilled,
  WarningFilled,
  CheckmarkCircleFilled,
  DismissCircleFilled,
  SpinnerIosRegular,
  BeakerRegular,
} from '@fluentui/react-icons';

export type DetailLevel = 'icon' | 'navigation' | 'compact' | 'detailed';
export type NodeStatus = 'default' | 'error' | 'warning' | 'mock' | 'running' | 'success' | 'failed' | 'skipped';
export type NodeShape = 'rectangle' | 'circle' | 'diamond' | 'hexagon';

// Per-status badge presentation. `styleKey` names a key in the makeStyles
// result so the lookup also drives the colour class. Statuses without an
// entry (e.g. 'skipped', 'mock') intentionally render no corner badge — see
// renderStatusBadge. 'mock' is special-cased to a labelled pill instead.
const STATUS_BADGE_CONFIG = {
  error:   { word: 'Error',     Icon: ErrorCircleFilled,     styleKey: 'statusBadgeError' },
  warning: { word: 'Warning',   Icon: WarningFilled,         styleKey: 'statusBadgeWarning' },
  running: { word: 'Running',   Icon: SpinnerIosRegular,     styleKey: 'statusBadgeRunning' },
  success: { word: 'Succeeded', Icon: CheckmarkCircleFilled, styleKey: 'statusBadgeSuccess' },
  failed:  { word: 'Failed',    Icon: DismissCircleFilled,   styleKey: 'statusBadgeFailed' },
} as const;

export interface FloatingNodeProps {
  /** The icon to display in the node */
  icon: ReactNode;
  /** Primary label (always visible except in icon-only mode) */
  label: string;
  /** Secondary label (shown below primary in compact/detailed modes) */
  subLabel?: string;
  /** Inline element rendered before the label text (e.g., clickable chevron) */
  labelPrefix?: ReactNode;
  /** Current detail level based on zoom */
  detailLevel: DetailLevel;
  /** Whether the node is selected */
  selected?: boolean;
  /** Node execution/validation status */
  status?: NodeStatus;
  /** Error/warning message to show in tooltip on the badge */
  statusMessage?: string | undefined;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** Click handler */
  onClick?: () => void;
  /** Keyboard handler */
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** Additional class name */
  className?: string;
  /** Node accent color for the icon background */
  accentColor?:
    | 'brand'
    | 'green'
    | 'berry'
    | 'purple'
    | 'teal'
    | 'marigold'
    | 'neutral'
    | 'agent-native'
    | 'agent-foundry'
    | 'agent-managed'
    | 'agent-hosted';
  /** Custom brand color override (hex string, e.g. '#1F85FF'). Overrides accentColor when provided. */
  brandColorOverride?: string | undefined;
  /** Node shape for visual differentiation */
  shape?: NodeShape;
  /** Whether to render a double outline on the icon box (e.g., for sub-workflows) */
  doubleOutline?: boolean;
  /** Tab index for roving tabindex pattern (0 for focusable, -1 for not) */
  tabIndex?: number;
  /** Children to render inside the node (handles, overlays) */
  children?: ReactNode;
}

/** Convert hex color to rgba string.
 *  Accepts "#RRGGBB", "RRGGBB", "#RGB", or "RGB". Falls back to black on invalid input.
 */
function hexToRgba(hex: string, alpha: number): string {
  const fallback = `rgba(0, 0, 0, ${alpha})`;

  if (!hex) {
    return fallback;
  }

  let value = hex.trim();
  if (value.startsWith('#')) {
    value = value.slice(1);
  }

  if (value.length !== 3 && value.length !== 6) {
    return fallback;
  }

  if (!/^[0-9a-fA-F]+$/.test(value)) {
    return fallback;
  }

  // Expand 3-digit hex to 6-digit (e.g. "0f8" -> "00ff88")
  if (value.length === 3) {
    value = value
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }

  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return fallback;
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const useStyles = makeStyles({
  // Wrapper for the entire node (icon + labels)
  // Width matches icon box so handles align with icon edges
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalSNudge,
    cursor: 'pointer',
    position: 'relative',
    width: '56px',
    overflow: 'visible',
    // Enter animation on node add — uses opacity only.
    // Avoid transform: scale() here because XY Flow measures handle
    // positions during mount; a scale transform shifts the handle's
    // getBoundingClientRect, causing edge lines to miss handle centers.
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMax,
    animationFillMode: 'backwards',
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
    },
  },

  // Tight positioning frame around the icon box. Holds the status badge as a
  // sibling so badges aren't clipped by shape-applied clip-paths (hexagon).
  iconBoxFrame: {
    position: 'relative',
    display: 'inline-block',
    lineHeight: 0,
  },

  // The icon container - subtle rounded square (default shape)
  iconBox: {
    width: '56px',
    height: '56px',
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border('1.5px', 'solid', 'transparent'),
    boxShadow: tokens.shadow4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transitionProperty: 'background-color, border-color, box-shadow, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    position: 'relative',

    // Gate hover styles behind pointer capability
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        backgroundColor: tokens.colorNeutralBackground3Hover,
        boxShadow: tokens.shadow8,
      },
    },
  },

  // Press feedback — works with both normal and rotated (diamond) transforms
  iconBoxPressable: {
    ':active': {
      transform: 'scale(0.96)',
    },
  },
  iconBoxPressableDiamond: {
    ':active': {
      transform: 'rotate(45deg) scale(0.93)',
    },
  },

  // Selected state
  iconBoxSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    boxShadow: `0 0 0 2px ${tokens.colorBrandBackground2}`,
  },

  // Selected + double outline combined
  iconBoxSelectedDoubleOutline: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    boxShadow: `0 0 0 2px ${tokens.colorBrandBackground2}, 0 0 0 4px ${tokens.colorNeutralStroke2}`,
  },

  // Status-based border colors.
  // Each variant pairs a coloured border (stripped in Windows high-contrast
  // mode) with a CSS system-color fallback so the status affordance survives
  // forced-colors. Pair this with the status badge icon below — colour alone
  // is non-conformant under WCAG 1.4.1.
  iconBoxError: {
    ...shorthands.borderColor(tokens.colorPaletteRedBorder1),
    '@media (forced-colors: active)': {
      ...shorthands.borderColor('LinkText'),
      forcedColorAdjust: 'auto',
    },
  },
  iconBoxWarning: {
    ...shorthands.borderColor(tokens.colorPaletteYellowBorder1),
    '@media (forced-colors: active)': {
      ...shorthands.borderColor('Mark'),
      forcedColorAdjust: 'auto',
    },
  },
  iconBoxRunning: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    // Simple pulsing effect using opacity
    animationName: {
      from: { opacity: 1 },
      to: { opacity: 0.6 },
    },
    animationDuration: '0.75s',
    animationIterationCount: 'infinite',
    animationDirection: 'alternate',
    animationTimingFunction: tokens.curveEasyEase,
    // Respect user's reduced motion preference
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      opacity: 1,
    },
    // The pulse is a box-shadow/colour effect that high-contrast strips —
    // Highlight gives a strong, system-aware border substitute.
    '@media (forced-colors: active)': {
      ...shorthands.borderColor('Highlight'),
      forcedColorAdjust: 'auto',
    },
  },
  iconBoxSuccess: {
    ...shorthands.borderColor(tokens.colorPaletteGreenBorder1),
    '@media (forced-colors: active)': {
      ...shorthands.borderColor('CanvasText'),
      forcedColorAdjust: 'auto',
    },
  },
  iconBoxFailed: {
    ...shorthands.borderColor(tokens.colorPaletteRedBorder1),
    '@media (forced-colors: active)': {
      ...shorthands.borderColor('LinkText'),
      forcedColorAdjust: 'auto',
    },
  },
  iconBoxMock: {
    ...shorthands.borderStyle('dashed'),
    ...shorthands.borderColor(ottoTokens.agentNativeBadgeForeground),
  },
  iconBoxSkipped: {
    ...shorthands.borderColor(tokens.colorNeutralStroke2),
    opacity: 0.45,
  },

  // Accent colors for icon background
  accentBrand: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  accentGreen: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  accentBerry: {
    backgroundColor: tokens.colorPaletteBerryBackground2,
  },
  accentPurple: {
    backgroundColor: tokens.colorPalettePurpleBackground2,
  },
  accentTeal: {
    backgroundColor: tokens.colorPaletteTealBackground2,
  },
  accentMarigold: {
    backgroundColor: tokens.colorPaletteMarigoldBackground2,
  },
  accentAgentNative: {
    // Background acts as the border color (visible through hexagon clip-path edges)
    backgroundColor: ottoTokens.agentNativeBadgeForeground,
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        backgroundColor: ottoTokens.agentNativeBadgeForeground,
      },
    },
    '::before': {
      backgroundColor: ottoTokens.agentNativeBadgeBackground,
      '@media (forced-colors: active)': {
        backgroundColor: 'Canvas',
      },
    },
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
    },
  },
  accentAgentFoundry: {
    backgroundColor: ottoTokens.agentFoundryBadgeForeground,
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        backgroundColor: ottoTokens.agentFoundryBadgeForeground,
      },
    },
    '::before': {
      backgroundColor: ottoTokens.agentFoundryBadgeBackground,
      '@media (forced-colors: active)': {
        backgroundColor: 'Canvas',
      },
    },
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
    },
  },
  accentAgentCoding: {
    backgroundColor: ottoTokens.agentCodingBadgeForeground,
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        backgroundColor: ottoTokens.agentCodingBadgeForeground,
      },
    },
    '::before': {
      backgroundColor: ottoTokens.agentCodingBadgeBackground,
      '@media (forced-colors: active)': {
        backgroundColor: 'Canvas',
      },
    },
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
    },
  },
  accentAgentHosted: {
    backgroundColor: ottoTokens.agentHostedBadgeForeground,
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        backgroundColor: ottoTokens.agentHostedBadgeForeground,
      },
    },
    '::before': {
      backgroundColor: ottoTokens.agentHostedBadgeBackground,
      '@media (forced-colors: active)': {
        backgroundColor: 'Canvas',
      },
    },
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
    },
  },
  // Agent-variant icon colors — keep the glyph readable on the lighter
  // pastel backgrounds (avoids the "transparent" look that comes from
  // a near-white `colorNeutralForeground1` on top of a low-alpha tint).
  iconColorAgentNative: {
    color: ottoTokens.agentNativeBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'HighlightText',
    },
  },
  iconColorAgentFoundry: {
    color: ottoTokens.agentFoundryBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'HighlightText',
    },
  },
  iconColorAgentCoding: {
    color: ottoTokens.agentCodingBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'HighlightText',
    },
  },
  iconColorAgentHosted: {
    color: ottoTokens.agentHostedBadgeForeground,
    '@media (forced-colors: active)': {
      color: 'HighlightText',
    },
  },
  accentNeutral: {
    backgroundColor: tokens.colorNeutralBackground3,
  },

  // Shape variants
  shapeRectangle: {
    borderRadius: tokens.borderRadiusLarge,
  },
  shapeCircle: {
    borderRadius: '50%',
  },
  shapeDiamond: {
    borderRadius: tokens.borderRadiusMedium,
    transform: 'rotate(45deg)',
  },
  // Counter-rotate icon content inside diamond shape
  diamondContent: {
    transform: 'rotate(-45deg)',
  },
  // Concentric border radius for icon content inside circle shape
  iconCircle: {
    borderRadius: tokens.borderRadiusXLarge,
    overflow: 'hidden',
  },
  // Hexagon shape for agent nodes - uses clip-path for true hexagon.
  // CSS border doesn't follow clip-path (only top/bottom flat edges show).
  // Instead, the parent background acts as the border color, and a ::before
  // pseudo-element (inset by border-width) provides the fill color.
  shapeHexagon: {
    borderRadius: tokens.borderRadiusNone,
    clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
    // Slightly larger to compensate for clipped corners
    width: '60px',
    height: '56px',
    // clip-path clips box-shadow, so use filter drop-shadow for elevation
    boxShadow: 'none',
    filter: `drop-shadow(0 1px 2px ${ottoTokens.nodeElevationShadow})`,
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        boxShadow: 'none',
      },
    },
    // Remove rectangular border — we simulate it via background + ::before
    ...shorthands.borderWidth('0'),
    '::before': {
      content: '""',
      position: 'absolute',
      top: tokens.strokeWidthThin,
      left: tokens.strokeWidthThin,
      right: tokens.strokeWidthThin,
      bottom: tokens.strokeWidthThin,
      clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
    },
  },

  // Icon itself
  icon: {
    fontSize: tokens.fontSizeBase600,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Ensure icon renders above hexagon's ::before fill pseudo-element
    position: 'relative',
    zIndex: 1,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
    maxWidth: '240px',
    width: 'max-content',
    lineHeight: '1.3',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
  },

  // Secondary label (even more muted)
  subLabel: {
    fontSize: '11px', // No exact Fluent token for 11px — intentional half-step
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    maxWidth: '240px',
    width: 'max-content',
    lineHeight: '1.2',
    marginTop: '-2px',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
  },

  // Double outline for sub-workflow nodes — "container inside" signal
  // Uses box-shadow instead of outline to avoid collision with :focus-visible
  iconBoxDoubleOutline: {
    boxShadow: `0 0 0 2px ${tokens.colorNeutralBackground1}, 0 0 0 4px ${tokens.colorNeutralStroke2}`,
  },

  // Label row with inline prefix (e.g., chevron). Mirrors `label` width
  // semantics — see comments above.
  labelWithPrefix: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    justifyContent: 'center',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center' as const,
    maxWidth: '240px',
    width: 'max-content',
    lineHeight: '1.3',
    overflowWrap: 'break-word' as const,
    wordBreak: 'normal' as const,
  },

  // Text portion inside labelWithPrefix flex container — lets ClampedLabel
  // shrink and truncate within the available space after the chevron icon.
  labelWithPrefixText: {
    flex: '1',
    minWidth: '0',
  },

  // Focus visible state
  focusVisible: {
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
    // High contrast mode support for accessibility
    '@media (forced-colors: active)': {
      ':focus-visible': {
        outline: '3px solid CanvasText',
        outlineOffset: '2px',
      },
    },
  },

  // Error/warning badge positioned at top-right of icon
  statusBadge: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    zIndex: 1,
  },

  statusBadgeError: {
    color: tokens.colorPaletteRedForeground1,
    '@media (forced-colors: active)': {
      color: 'LinkText',
      forcedColorAdjust: 'auto',
    },
  },

  statusBadgeWarning: {
    color: tokens.colorPaletteYellowForeground1,
    '@media (forced-colors: active)': {
      color: 'Mark',
      forcedColorAdjust: 'auto',
    },
  },

  statusBadgeRunning: {
    color: tokens.colorBrandForeground1,
    '@media (forced-colors: active)': {
      color: 'Highlight',
      forcedColorAdjust: 'auto',
    },
  },

  statusBadgeSuccess: {
    color: tokens.colorPaletteGreenForeground1,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
      forcedColorAdjust: 'auto',
    },
  },

  statusBadgeFailed: {
    color: tokens.colorPaletteRedForeground1,
    '@media (forced-colors: active)': {
      color: 'LinkText',
      forcedColorAdjust: 'auto',
    },
  },

  // Mock status — a labelled pill above the top edge of the node rather than a
  // tiny corner icon, so the "this node is mocked" state reads at a glance.
  mockPill: {
    position: 'absolute',
    top: '-20px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: '1px 6px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: ottoTokens.agentNativeBadgeBackground,
    color: ottoTokens.agentNativeBadgeForeground,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '14px',
    letterSpacing: '0.02em',
    boxShadow: tokens.shadow4,
    whiteSpace: 'nowrap',
    zIndex: 2,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
      color: 'HighlightText',
      forcedColorAdjust: 'auto',
    },
  },
  mockPillIcon: {
    fontSize: tokens.fontSizeBase100,
    display: 'flex',
  },

  statusIcon: {
    fontSize: tokens.fontSizeBase300,
  },

  // Spin the running spinner so it reads as motion (paired with iconBox pulse).
  // Disabled under prefers-reduced-motion — the badge is still visible, just static.
  statusIconRunning: {
    animationName: {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    animationDuration: '1s',
    animationIterationCount: 'infinite',
    animationTimingFunction: tokens.curveLinear,
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
    },
  },
});

export const FloatingNode = memo(
  forwardRef<HTMLDivElement, FloatingNodeProps>(
    (
      {
        icon,
        label,
        subLabel,
        labelPrefix,
        detailLevel,
        selected = false,
        status = 'default',
        statusMessage,
        ariaLabel,
        onClick,
        onKeyDown,
        className,
        accentColor = 'neutral',
        brandColorOverride,
        shape = 'rectangle',
        doubleOutline = false,
        tabIndex = 0,
        children,
      },
      ref
    ) => {
      const styles = useStyles();
      const [isHovered, setIsHovered] = useState(false);
      const handleMouseEnter = useCallback(() => {
        if (brandColorOverride) setIsHovered(true);
      }, [brandColorOverride]);
      const handleMouseLeave = useCallback(() => {
        if (brandColorOverride) setIsHovered(false);
      }, [brandColorOverride]);

      // Get status class
      const getStatusClass = () => {
        switch (status) {
          case 'error':
            return styles.iconBoxError;
          case 'warning':
            return styles.iconBoxWarning;
          case 'mock':
            return styles.iconBoxMock;
          case 'running':
            return styles.iconBoxRunning;
          case 'success':
            return styles.iconBoxSuccess;
          case 'failed':
            return styles.iconBoxFailed;
          case 'skipped':
            return styles.iconBoxSkipped;
          default:
            return undefined;
        }
      };

      // Render status badge for any non-default state.
      // Pairs every colour-only border cue with a distinct icon shape so the
      // status is recognisable without colour (WCAG 1.4.1) and survives
      // Windows high-contrast mode (icon glyphs render even when colours
      // are stripped).
      const renderStatusBadge = () => {
        if (status === 'default') return null;

        // Mock status uses a labelled pill rather than the corner icon badge
        // so it is clearly legible on the canvas (template mode).
        if (status === 'mock') {
          const tooltipText = statusMessage || 'This action uses test data during test runs';
          const mockPill = (
            <div
              className={styles.mockPill}
              role="img"
              aria-label={statusMessage ? `Test: ${statusMessage}` : 'Test'}
              tabIndex={0}
            >
              <BeakerRegular className={styles.mockPillIcon} aria-hidden="true" />
              <span>Test</span>
            </div>
          );
          return (
            <Tooltip content={tooltipText} relationship="label" positioning="above">
              {mockPill}
            </Tooltip>
          );
        }

        // Per-status presentation lookup. `styleKey` indexes into the
        // makeStyles result so we keep all status-specific data in one
        // place instead of duplicating switch/case ladders. Statuses
        // without a config entry (e.g. 'skipped', or any future status
        // added to NodeStatus before its badge is wired up) render no
        // badge — emitting an empty role="img" element would be a
        // WCAG 4.1.2 (Name, Role, Value) failure.
        const config = STATUS_BADGE_CONFIG[status as keyof typeof STATUS_BADGE_CONFIG];
        if (!config) return null;

        const Icon = config.Icon;
        const statusWord = config.word;
        const badgeClasses = mergeClasses(styles.statusBadge, styles[config.styleKey]);
        const iconClass =
          status === 'running'
            ? mergeClasses(styles.statusIcon, styles.statusIconRunning)
            : styles.statusIcon;
        const iconElement = <Icon className={iconClass} aria-hidden="true" />;

        // When a status message is provided, expose it via tooltip + label
        // so keyboard/SR users can read it.
        if (statusMessage) {
          return (
            <Tooltip content={statusMessage} relationship="label" positioning="above">
              <div
                className={badgeClasses}
                role="img"
                aria-label={`${statusWord}: ${statusMessage}`}
                tabIndex={0}
              >
                {iconElement}
              </div>
            </Tooltip>
          );
        }

        // No message — still expose the status word as the accessible name
        // so screen readers announce running/success/failed/etc.
        return (
          <div className={badgeClasses} role="img" aria-label={statusWord}>
            {iconElement}
          </div>
        );
      };

      // Get accent color class
      const accentClassMap: Record<NonNullable<typeof accentColor>, string> = {
        brand: styles.accentBrand,
        green: styles.accentGreen,
        berry: styles.accentBerry,
        purple: styles.accentPurple,
        teal: styles.accentTeal,
        marigold: styles.accentMarigold,
        'agent-native': styles.accentAgentNative,
        'agent-foundry': styles.accentAgentFoundry,
        'agent-managed': styles.accentAgentCoding,
        'agent-hosted': styles.accentAgentHosted,
        neutral: styles.accentNeutral,
      };
      const getAccentClass = () => accentClassMap[accentColor] ?? styles.accentNeutral;

      // Get shape class
      const getShapeClass = () => {
        switch (shape) {
          case 'circle':
            return styles.shapeCircle;
          case 'diamond':
            return styles.shapeDiamond;
          case 'hexagon':
            return styles.shapeHexagon;
          default:
            return styles.shapeRectangle;
        }
      };

      // Compute inline styles for brand color override.
      // Unified opacity across all shapes — consistency creates a cohesive canvas.
      // Hover shifts opacity slightly to provide surface feedback.
      // Border color only applies when no status is active — status borders
      // (error/warning/running/success/failed) take precedence via CSS classes.
      const hasActiveStatus = status !== 'default' && status !== 'mock';
      const iconBoxStyle = useMemo((): React.CSSProperties | undefined => {
        if (!brandColorOverride) return undefined;

        const baseOpacity = isHovered ? 0.58 : 0.5;

        if (selected) {
          return {
            backgroundColor: hexToRgba(brandColorOverride, 0.6),
            ...(!hasActiveStatus && { borderColor: brandColorOverride }),
            boxShadow: `0 0 0 2px ${hexToRgba(brandColorOverride, 0.3)}`,
          };
        }
        return {
          backgroundColor: hexToRgba(brandColorOverride, baseOpacity),
          ...(!hasActiveStatus && { borderColor: brandColorOverride }),
        };
      }, [brandColorOverride, selected, isHovered, hasActiveStatus]);

      const getOutlineClass = () => {
        if (selected && doubleOutline) return styles.iconBoxSelectedDoubleOutline;
        if (selected) return styles.iconBoxSelected;
        if (doubleOutline) return styles.iconBoxDoubleOutline;
        return undefined;
      };

      // When brandColorOverride is set, skip the accent class to avoid
      // competing background styles (inline style takes precedence anyway,
      // but this keeps the class list clean).
      // Accent comes AFTER the outline class so a non-neutral accent (e.g.
      // teal for native agents, purple for Foundry) wins the background
      // when the node is selected — selection still reads as the brand
      // border + box-shadow ring from `iconBoxSelected`. Neutral-accent
      // nodes skip the accent class entirely so they keep the existing
      // brand-tinted selection background.
      const accentClassIfAny =
        !brandColorOverride && accentColor !== 'neutral' ? getAccentClass() : undefined;
      const agentIconColorMap: Record<string, string> = {
        'agent-native': styles.iconColorAgentNative,
        'agent-foundry': styles.iconColorAgentFoundry,
        'agent-managed': styles.iconColorAgentCoding,
        'agent-hosted': styles.iconColorAgentHosted,
      };
      const iconColorClassIfAny = agentIconColorMap[accentColor];
      const iconBoxClasses = mergeClasses(
        styles.iconBox,
        shape === 'diamond' ? styles.iconBoxPressableDiamond : styles.iconBoxPressable,
        getShapeClass(),
        getOutlineClass(),
        accentClassIfAny,
        getStatusClass()
      );

      // Icon-only mode: just the icon box, tooltip on hover
      if (detailLevel === 'icon') {
        return (
          <Tooltip
            content={`${label}${subLabel ? ` - ${subLabel}` : ''}`}
            relationship="description"
            positioning="below"
          >
            <div
              ref={ref}
              className={mergeClasses(styles.wrapper, styles.focusVisible, className)}
              role="group"
              aria-roledescription="workflow node"
              tabIndex={tabIndex}
              aria-label={ariaLabel ?? label}
              onClick={onClick}
              onKeyDown={onKeyDown}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className={styles.iconBoxFrame}>
                <div className={iconBoxClasses} style={iconBoxStyle}>
                  <div className={mergeClasses(styles.icon, shape === 'diamond' && styles.diamondContent, shape === 'circle' && styles.iconCircle, iconColorClassIfAny)}>
                    {icon}
                  </div>
                </div>
                {/* Sibling of iconBox so shape clip-paths (hexagon) don't hide the badge. */}
                {renderStatusBadge()}
              </div>
              {children}
            </div>
          </Tooltip>
        );
      }

      // Compact mode: icon + primary label
      // Detailed mode: icon + primary label + sub-label
      return (
        <div
          ref={ref}
          className={mergeClasses(styles.wrapper, styles.focusVisible, className)}
          role="group"
          aria-roledescription="workflow node"
          tabIndex={tabIndex}
          aria-label={ariaLabel ?? label}
          onClick={onClick}
          onKeyDown={onKeyDown}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className={styles.iconBoxFrame}>
            <div className={iconBoxClasses} style={iconBoxStyle}>
              <div className={mergeClasses(styles.icon, shape === 'diamond' && styles.diamondContent, shape === 'circle' && styles.iconCircle, iconColorClassIfAny)}>
                {icon}
              </div>
            </div>
            {/* Sibling of iconBox so shape clip-paths (hexagon) don't hide the badge. */}
            {renderStatusBadge()}
          </div>
          {labelPrefix ? (
            <div className={styles.labelWithPrefix}>
              {labelPrefix}
              <ClampedLabel text={label} lines={2} className={styles.labelWithPrefixText} />
            </div>
          ) : (
            <ClampedLabel text={label} lines={2} className={styles.label} />
          )}
          {subLabel && (detailLevel === 'compact' || detailLevel === 'detailed') && (
            <ClampedLabel text={subLabel} lines={1} className={styles.subLabel} />
          )}
          {children}
        </div>
      );
    }
  )
);

FloatingNode.displayName = 'FloatingNode';
